import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Paper,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Delete from '@mui/icons-material/Delete'
import React, { useContext, useEffect, useState } from 'react'
import safetyCheckQuestionTemplate from '../../../fleetEquipment/vehicles/SafetyCheck/safetyCheckQuestions.js'
import API from '../../../../services/API.js'
import { GlobalContext } from '../../../../App.js'

//
// EDITABLE QUESTION
//
const EditableQuestion = React.memo(function EditableQuestion({
  value,
  onCommit,
}) {
  const [localValue, setLocalValue] = useState(value)

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  return (
    <TextField
      variant="outlined"
      size="small"
      fullWidth
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={() => onCommit(localValue)}
      sx={{
        background: '#fff',
        borderRadius: 1,
      }}
    />
  )
})

//
// CATEGORY BLOCK
//
const Category = React.memo(({ cat, items, handleChange, handleDelete }) => {
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {cat}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          {items.map((q, i) => (
            <Stack
              key={`${cat}-${i}`}
              direction="row"
              alignItems="center"
              spacing={1}
            >
              <EditableQuestion
                value={q}
                onCommit={(newValue) => handleChange(cat, i, newValue)}
              />
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDelete(cat, i)}
              >
                <Delete />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
})

//
// MAIN PAGE
//
const VehicleSafetyCheckSettings = () => {
  const [loading, setLoading] = useState(true)
  const [changesPresent, setChangesPresent] = useState(false)
  const [frequency, setFrequency] = useState('6')
  const [questions, setQuestions] = useState(safetyCheckQuestionTemplate ?? {})
  const [hasCustomQuestions, setHasCustomQuestions] = useState(false)
  const { setToast } = useContext(GlobalContext)

  const handleChange = (category, index, newValue) => {
    setQuestions((prev) => ({
      ...prev,
      [category]: prev[category].map((item, idx) =>
        idx === index ? newValue : item,
      ),
    }))
    !hasCustomQuestions && setHasCustomQuestions(true)
    setChangesPresent(true)
  }

  const handleDelete = (category, index) => {
    setQuestions((prev) => ({
      ...prev,
      [category]: prev[category].filter((_, idx) => idx !== index),
    }))

    setHasCustomQuestions(true)
    setChangesPresent(true)
  }

  const handleSaveChanges = () => {
    const data = {
      vehicleSafetyCheckFrequency: frequency,
    }
    if (hasCustomQuestions) {
      data.vehicleSafetyCheckCustomQuestions = questions
    }
    API.put('/site-config', { ...data }).then((res) => {
      if (res.status === 200) {
        setToast({
          message: 'Changes Saved',
        })
        setChangesPresent(false)
      }
    })
  }

  useEffect(() => {
    API.get(
      '/settings?select=vehicleSafetyCheckFrequency vehicleSafetyCheckCustomQuestions',
    ).then((res) => {
      if (res.status === 200) {
        console.log('res.data', res.data)
        setFrequency(res.data.vehicleSafetyCheckFrequency)
        if (res.data.vehicleSafetyCheckCustomQuestions) {
          setQuestions(res.data.vehicleSafetyCheckCustomQuestions)
          setHasCustomQuestions(true)
        }
        setLoading(false)
      }
    })
  }, [])

  return (
    <Stack spacing={2}>
      {/* HEADER */}
      <Stack>
        <Typography variant="title2" fontWeight={700}>
          Vehicle Safety Check Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure your organisation’s safety check schedule and customise the
          form used by your garage.
        </Typography>
        {loading && <LinearProgress sx={{ mt: 2 }} />}
      </Stack>

      {/* FREQUENCY CARD */}
      <Card elevation={1} sx={{ borderRadius: 3 }}>
        <CardHeader
          title="Safety Check Frequency"
          titleTypographyProps={{
            variant: 'title3',
            color: 'anBlue',
            fontWeight: 600,
          }}
        />
        <Divider />
        <CardContent>
          <FormControl fullWidth>
            <InputLabel>Safety Check Frequency</InputLabel>
            <Select
              value={frequency}
              onChange={(e) => {
                setFrequency(e.target.value)
                setChangesPresent(true)
              }}
              label="Safety Check Frequency"
            >
              <MenuItem value="1">Every Month</MenuItem>
              <MenuItem value="2">Every Two Months</MenuItem>
              <MenuItem value="3">Every Three Months</MenuItem>
              <MenuItem value="6">Every Six Months</MenuItem>
              <MenuItem value="12">Every Year</MenuItem>
              <MenuItem value="18">Every 18 Months</MenuItem>
              <MenuItem value="24">Every Two Years</MenuItem>
              <MenuItem value="Disabled">Disabled</MenuItem>
            </Select>
            <FormHelperText>
              How often should safety checks be completed?
            </FormHelperText>
          </FormControl>
        </CardContent>
      </Card>

      {/* WARN IF DISABLED */}
      {frequency === 'Disabled' ? (
        <Alert severity="info">
          In-Depth Garage Safety Checks have been disabled.
        </Alert>
      ) : (
        <Card elevation={1} sx={{ borderRadius: 3 }}>
          <CardHeader
            title="Safety Check Form"
            titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
            subheader="Customise the questions that appear on your garage inspection checklist."
          />
          <Divider />
          <CardContent>
            <div>
              {Object.keys(questions).map((cat) => (
                <Category
                  key={cat}
                  cat={cat}
                  items={questions[cat]}
                  handleChange={handleChange}
                  handleDelete={handleDelete}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* STICKY SAVE BAR */}
      <Paper
        elevation={3}
        sx={{
          position: 'sticky',
          bottom: 0,
          p: 2,
          borderRadius: 3,
          background: '#ffffffcc',
          backdropFilter: 'blur(10px)',
          border: '1px solid #eee',
        }}
      >
        <Button
          size="large"
          variant="contained"
          fullWidth
          color="success"
          onClick={handleSaveChanges}
          disabled={!changesPresent}
        >
          Save Changes
        </Button>
      </Paper>
    </Stack>
  )
}

export default VehicleSafetyCheckSettings
